import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { FinancialDocumentDetail, InstallmentInvoiceInfo } from 'generated/bffFinancialInfo/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { GetFinancialDocumentDetailPayload } from 'models/Billing';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';

/* STATE */
export interface BillingDetailState {
  financialDocumentList: FinancialDocumentDetail[];
  financialDocumentDetail: FinancialDocumentDetail | null;
  interactionInvoiceData: InstallmentInvoiceInfo | null;
  loadingStatus: LoadingStatus;
  financialInvoiceNumExt: string;
}

/* ACTION TYPES */
export enum BillingDetailActionTypes {
  SetLoading = '@@BillingDetail/SET_LOADING',
  GetFinancialDocumentList = '@@BillingDetail/GET_FINANCIAL_DOCUMENT_LIST',
  GetFinancialDocumentDetail = '@@BillingDetail/GET_FINANCIAL_DOCUMENT_DETAIL',
  GetInteractionInvoiceData = '@@BillingDetail/GET_INVOICE_INTERACTION_DATA',
  ClearFinancialDocumentDetail = '@@BillingDetail/CLEAR_FINANCIAL_DOCUMENT_DETAIL',
}

/* ACTIONS */
export const setLoadingAction = createActionCreator(BillingDetailActionTypes.SetLoading);

export const getFinancialDocumentListActions = createApiActionCreators(
  BillingDetailActionTypes.GetFinancialDocumentList
);

export const getFinancialDocumentDetailActions = createApiActionCreators(
  BillingDetailActionTypes.GetFinancialDocumentDetail
);

export const getInteractionInvoiceDataActions = createApiActionCreators(
  BillingDetailActionTypes.GetInteractionInvoiceData
);

export const clearFinancialDocumentDetailAction = createActionCreator(
  BillingDetailActionTypes.ClearFinancialDocumentDetail
);

/* REDUCERS */
const initialState: BillingDetailState = {
  financialDocumentList: [],
  financialDocumentDetail: null,
  interactionInvoiceData: null,
  loadingStatus: LoadingStatus.loading,
  financialInvoiceNumExt: '',
};

const financialDocumentList = createReducer(initialState.financialDocumentList, {
  [BillingDetailActionTypes.GetFinancialDocumentList]: {
    [RequestActionTypes.SUCCESS]: (state: FinancialDocumentDetail[], payload: any) =>
      payload.financialDocumentBasicList,
    [RequestActionTypes.FAILURE]: () => initialState.financialDocumentList,
  },
});

const financialDocumentDetail = createReducer(initialState.financialDocumentDetail, {
  [BillingDetailActionTypes.GetFinancialDocumentDetail]: {
    [RequestActionTypes.SUCCESS]: (state: FinancialDocumentDetail, payload: any) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
  [BillingDetailActionTypes.ClearFinancialDocumentDetail]: () => null,
});

const financialInvoiceNumExt = createReducer(initialState.financialInvoiceNumExt, {
  [BillingDetailActionTypes.GetFinancialDocumentDetail]: {
    [RequestActionTypes.SUCCESS]: (state: FinancialDocumentDetail, payload: FinancialDocumentDetail) =>
      payload.invoiceNumExt || '',
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const interactionInvoiceData = createReducer(initialState.interactionInvoiceData, {
  [BillingDetailActionTypes.GetInteractionInvoiceData]: {
    [RequestActionTypes.SUCCESS]: (state: InstallmentInvoiceInfo, payload: any) => payload,
    [RequestActionTypes.FAILURE]: () => initialState.interactionInvoiceData,
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [BillingDetailActionTypes.GetFinancialDocumentList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [BillingDetailActionTypes.GetFinancialDocumentDetail]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [BillingDetailActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

export default combineReducers<BillingDetailState>({
  financialDocumentList,
  financialDocumentDetail,
  interactionInvoiceData,
  loadingStatus,
  financialInvoiceNumExt,
});

/* SELECTORS*/
const selectBillingDetail = (state: AppState) => state.billingDetail;

export const selectBillingDetailLoading = (state: AppState): LoadingStatus => selectBillingDetail(state).loadingStatus;

export const selectBillingDetailFinancialDocumentList = (state: AppState): FinancialDocumentDetail[] =>
  selectBillingDetail(state).financialDocumentList;

export const selectBillingDetailFinancialDocumentDetail = (state: AppState): FinancialDocumentDetail | null =>
  selectBillingDetail(state).financialDocumentDetail;

export const selectFinancialDocumentInvoiceNumExt = (state: AppState): string =>
  selectBillingDetail(state).financialInvoiceNumExt;

/* SAGAS */
function* getFinancialDocumentList({ payload: caRefNo }: AppAction<string>) {
  const pageSize = Number(selectProperty('gui.financialDocumentList.pageSize'));
  const pagesCount = Number(selectProperty('gui.financialDocumentList.loadPagesCount.Init'));
  const customer: CustomerInfo = yield select(selectContextCustomerInfo);

  const request: ApiClientRequest = {
    apiCall: call(
      api.getFinancialDocumentList,
      yield getApiUrl(environment.urlConfig.financialInfo),
      pageSize * pagesCount,
      caRefNo,
      customer.cuRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getFinancialDocumentListActions.success(resp.data));
  } else {
    yield put(getFinancialDocumentListActions.failure());

    if (resp.status === 404) {
      yield put(getKahlActions.request());
    }
  }
}

function* getFinancialDocumentDetail({ payload }: AppAction<GetFinancialDocumentDetailPayload>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.getFinancialDocumentDetail,
      yield getApiUrl(environment.urlConfig.financialInfo),
      payload.caRefNo,
      payload.documentId,
      payload.documentId ? undefined : payload.invoiceNumExt,
      payload.documentId || payload.invoiceNumExt ? undefined : payload.varSymbol
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getFinancialDocumentDetailActions.success(resp.data));
  } else {
    yield put(getFinancialDocumentDetailActions.failure());

    if (resp.status === 404) {
      yield put(getKahlActions.request());
    }
  }
}

function* getInteractionInvoiceData({ payload: invoiceExtNum }: AppAction<string>) {
  const pageSize = Number(selectProperty('gui.billing.monthDetail.interactiveInvoice.pageSize'));

  const request: ApiClientRequest = {
    apiCall: call(
      api.getInteractionInvoiceData,
      yield getApiUrl(environment.urlConfig.financialInfo),
      invoiceExtNum,
      pageSize
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getInteractionInvoiceDataActions.success(resp.data));
  } else {
    yield put(getInteractionInvoiceDataActions.failure());

    if (resp.status === 404) {
      yield put(getKahlActions.request());
    }
  }
}

/* EXPORT */
export function* billingDetailSaga() {
  yield takeLatest(
    createActionType(BillingDetailActionTypes.GetFinancialDocumentList, RequestActionTypes.REQUEST),
    getFinancialDocumentList
  );

  yield takeLatest(
    createActionType(BillingDetailActionTypes.GetFinancialDocumentDetail, RequestActionTypes.REQUEST),
    getFinancialDocumentDetail
  );

  yield takeLatest(
    createActionType(BillingDetailActionTypes.GetInteractionInvoiceData, RequestActionTypes.REQUEST),
    getInteractionInvoiceData
  );
}
